import jQuery from 'jquery';

import { WindowEventEmitterInstance } from '../../window-event-emitter';

const CSS_PARALLAX_BACKGROUND_SELECTOR = '.et-pb-parallax-background--css';
const CSS_PARALLAX_BACKGROUND_CLASS = 'et-pb-parallax-background--css';
const CSS_PARALLAX_EMULATED_ATTRIBUTE = 'data-divi-css-parallax-animation-emulated';

const CSS_PARALLAX_EMULATION_STYLE_PROPERTIES: string[] = ['width', 'height', 'top', 'left', 'background-attachment'];

const CSS_PARALLAX_VIEWPORT_SYNC_LISTENER_ID = 'cssParallaxAnimationEmulation';

const syncedCssParallaxLayers = new Set<HTMLElement>();
const emulatedCssParallaxLayers = new Set<HTMLElement>();

const cssParallaxEmulationState = new WeakMap<
  HTMLElement,
  {
    owners: Set<HTMLElement>;
    syncOwners: Set<HTMLElement>;
    parentDocumentTop: number;
    parentDocumentLeft: number;
  }
>();

let isViewportSyncAttached = false;

/**
 * Whether iOS WebKit will force CSS parallax onto true parallax.
 *
 * PHP still emits `.et-pb-parallax-background--css` on that span. `initParallax` then
 * registers JavaScript parallax, which owns inline `height` and `transform`.
 * Duplicated here because script-library cannot import `@divi/ui-library`.
 *
 * @since ??
 *
 * @returns {boolean} True when CSS-method layers are not native `fixed` parallax.
 */
const isIOSWebKit = (): boolean => {
  if ('undefined' === typeof window) {
    return false;
  }

  const { userAgent, platform } = window.navigator;

  return /AppleWebKit/i.test(userAgent) && (/iphone|ipad/i.test(userAgent) || /iphone|ipad/i.test(platform));
};

/**
 * Whether JavaScript parallax already owns this span.
 *
 * `setParallaxStyle` writes an inline transform. CSS parallax emulation never does.
 *
 * @since ??
 *
 * @param {HTMLElement} layer Parallax background span.
 *
 * @returns {boolean} True when true parallax sizing must be left alone.
 */
const isJavaScriptParallaxLayer = (layer: HTMLElement): boolean => '' !== layer.style.transform;

const isCssMethodLayer = (layer: HTMLElement): boolean => layer.classList.contains(CSS_PARALLAX_BACKGROUND_CLASS);

const shouldAbandonEmulatedLayer = (layer: HTMLElement): boolean =>
  isJavaScriptParallaxLayer(layer) || !isCssMethodLayer(layer);

const getCssParallaxLayers = ($element: JQuery): JQuery =>
  $element.find(CSS_PARALLAX_BACKGROUND_SELECTOR).add($element.filter(CSS_PARALLAX_BACKGROUND_SELECTOR));

const getViewportMetrics = (): { viewportWidth: number; viewportHeight: number; scrollTop: number } => {
  const $window = jQuery(window);

  return {
    viewportWidth: $window.width() ?? 0,
    viewportHeight: $window.innerHeight() ?? 0,
    scrollTop: $window.scrollTop() ?? 0,
  };
};

const applyEmulationStyles = (
  $layer: JQuery,
  viewportWidth: number,
  viewportHeight: number,
  scrollTop: number,
  parentDocumentTop: number,
  parentDocumentLeft: number,
): void => {
  $layer.css({
    width: `${viewportWidth}px`,
    height: `${viewportHeight}px`,
    top: `${scrollTop - parentDocumentTop}px`,
    left: `${0 - parentDocumentLeft}px`,
    'background-attachment': 'scroll',
  });
  $layer.attr(CSS_PARALLAX_EMULATED_ATTRIBUTE, 'true');
};

const clearEmulationStyles = (layer: HTMLElement): void => {
  CSS_PARALLAX_EMULATION_STYLE_PROPERTIES.forEach(property => {
    layer.style.removeProperty(property);
  });
  layer.removeAttribute(CSS_PARALLAX_EMULATED_ATTRIBUTE);
};

const clearCropStylesKeepingHeight = (layer: HTMLElement): void => {
  ['width', 'top', 'left', 'background-attachment'].forEach(property => {
    layer.style.removeProperty(property);
  });
  layer.removeAttribute(CSS_PARALLAX_EMULATED_ATTRIBUTE);
};

const syncEmulatedCssParallaxLayers = (): void => {
  const { viewportWidth, viewportHeight, scrollTop } = getViewportMetrics();

  syncedCssParallaxLayers.forEach((layer: HTMLElement) => {
    if (!layer.isConnected) {
      emulatedCssParallaxLayers.delete(layer);
      syncedCssParallaxLayers.delete(layer);
      cssParallaxEmulationState.delete(layer);
      if (0 === syncedCssParallaxLayers.size) {
        detachViewportSync();
      }
      return;
    }

    if (shouldAbandonEmulatedLayer(layer)) {
      abandonEmulatedLayer(layer);
      return;
    }

    const state = cssParallaxEmulationState.get(layer);

    if (!state || 0 === state.syncOwners.size) {
      return;
    }

    applyEmulationStyles(
      jQuery(layer),
      viewportWidth,
      viewportHeight,
      scrollTop,
      state.parentDocumentTop,
      state.parentDocumentLeft,
    );
  });
};

const attachViewportSync = (): void => {
  if (isViewportSyncAttached) {
    return;
  }

  WindowEventEmitterInstance.addWindowListener(
    'top.changed',
    syncEmulatedCssParallaxLayers,
    CSS_PARALLAX_VIEWPORT_SYNC_LISTENER_ID,
  );
  WindowEventEmitterInstance.addWindowListener(
    'width.changed',
    syncEmulatedCssParallaxLayers,
    CSS_PARALLAX_VIEWPORT_SYNC_LISTENER_ID,
  );
  WindowEventEmitterInstance.addWindowListener(
    'height.changed',
    syncEmulatedCssParallaxLayers,
    CSS_PARALLAX_VIEWPORT_SYNC_LISTENER_ID,
  );
  isViewportSyncAttached = true;
};

/**
 * Remove viewport sync listeners when no looping layers remain.
 *
 * @since ??
 *
 * @returns {void}
 */
function detachViewportSync(): void {
  if (!isViewportSyncAttached) {
    return;
  }

  WindowEventEmitterInstance.removeWindowListener(
    'top.changed',
    syncEmulatedCssParallaxLayers,
    CSS_PARALLAX_VIEWPORT_SYNC_LISTENER_ID,
  );
  WindowEventEmitterInstance.removeWindowListener(
    'width.changed',
    syncEmulatedCssParallaxLayers,
    CSS_PARALLAX_VIEWPORT_SYNC_LISTENER_ID,
  );
  WindowEventEmitterInstance.removeWindowListener(
    'height.changed',
    syncEmulatedCssParallaxLayers,
    CSS_PARALLAX_VIEWPORT_SYNC_LISTENER_ID,
  );
  isViewportSyncAttached = false;
}

const releaseSyncedLayer = (layer: HTMLElement): void => {
  syncedCssParallaxLayers.delete(layer);

  if (0 === syncedCssParallaxLayers.size) {
    detachViewportSync();
  }
};

/**
 * Stop emulation when a layer is no longer CSS parallax.
 *
 * True parallax writes inline `transform` and `height`. Keep that height, and stop
 * rewriting the viewport crop. A class removal without a transform is a full clear.
 *
 * @since ??
 *
 * @param {HTMLElement} layer Parallax background span.
 *
 * @returns {void}
 */
function abandonEmulatedLayer(layer: HTMLElement): void {
  const keepHeight = isJavaScriptParallaxLayer(layer);

  emulatedCssParallaxLayers.delete(layer);
  cssParallaxEmulationState.delete(layer);
  releaseSyncedLayer(layer);

  if (keepHeight) {
    clearCropStylesKeepingHeight(layer);
    return;
  }

  clearEmulationStyles(layer);
}

const releaseOwnerFromLayer = (layer: HTMLElement, owner: HTMLElement): void => {
  const state = cssParallaxEmulationState.get(layer);

  if (!state) {
    return;
  }

  if (shouldAbandonEmulatedLayer(layer)) {
    abandonEmulatedLayer(layer);
    return;
  }

  state.owners.delete(owner);
  state.syncOwners.delete(owner);

  if (0 === state.syncOwners.size) {
    releaseSyncedLayer(layer);
  }

  if (0 < state.owners.size) {
    return;
  }

  emulatedCssParallaxLayers.delete(layer);
  cssParallaxEmulationState.delete(layer);
  releaseSyncedLayer(layer);
  clearEmulationStyles(layer);
};

/**
 * Whether this animation style applies a transform (inline intensity or keyframes).
 *
 * Fade-center is opacity-only and must keep native `background-attachment: fixed`.
 *
 * @since ??
 *
 * @param {string} animationStyle Animation style from `data-animation-style`.
 *
 * @returns {boolean} True when a transform will create a containing block.
 */
export const shouldEmulateCssParallaxForAnimation = (animationStyle: string): boolean => 'fade' !== animationStyle;

/**
 * Emulate viewport-sized CSS parallax while an ancestor transform is about to run.
 *
 * Offsets are measured from untransformed layout. Call this before writing animation `transform`.
 * Nested transform animations share the first untransformed crop until the last owner restores.
 * Pass `true` for looping animations so the crop stays viewport-sized on scroll and resize.
 * IOS WebKit and spans that already have a true-parallax transform are left unchanged..
 *
 * @since ??
 *
 * @param {JQuery} $element Animated element that may contain CSS parallax layers.
 * @param {boolean} syncWithViewport Whether to keep offsets aligned after the initial measure.
 *
 * @returns {void}
 */
export const applyCssParallaxAnimationEmulation = ($element: JQuery, syncWithViewport = false): void => {
  if (isIOSWebKit()) {
    return;
  }

  const owner = $element.get(0);
  const $layers = getCssParallaxLayers($element);

  if (!(owner instanceof HTMLElement) || 0 === $layers.length) {
    return;
  }

  const { viewportWidth, viewportHeight, scrollTop } = getViewportMetrics();

  $layers.each((_, layer) => {
    if (!(layer instanceof HTMLElement) || isJavaScriptParallaxLayer(layer)) {
      return;
    }

    const existingState = cssParallaxEmulationState.get(layer);

    if (existingState) {
      existingState.owners.add(owner);

      if (syncWithViewport) {
        existingState.syncOwners.add(owner);
        syncedCssParallaxLayers.add(layer);
      }

      emulatedCssParallaxLayers.add(layer);
      return;
    }

    const $layer = jQuery(layer);
    const parentOffset = $layer.parent().offset();

    if (!parentOffset) {
      return;
    }

    applyEmulationStyles($layer, viewportWidth, viewportHeight, scrollTop, parentOffset.top, parentOffset.left);

    const syncOwners = new Set<HTMLElement>();

    if (syncWithViewport) {
      syncOwners.add(owner);
      syncedCssParallaxLayers.add(layer);
    }

    emulatedCssParallaxLayers.add(layer);
    cssParallaxEmulationState.set(layer, {
      owners: new Set([owner]),
      syncOwners,
      parentDocumentTop: parentOffset.top,
      parentDocumentLeft: parentOffset.left,
    });
  });

  if (syncWithViewport && 0 < syncedCssParallaxLayers.size) {
    attachViewportSync();
  }
};

/**
 * Strip CSS parallax emulation so stylesheet `background-attachment: fixed` resumes.
 *
 * Call this after animation `transform` has been cleared (`removeAnimation`, interaction
 * `resetAnimationState`, and Visual Builder preview stripping). Shared layers stay emulated until
 * every nested transform animation that claimed them has restored.
 * Layers this helper never claimed, including iOS true parallax, keep their inline styles.
 *
 * @since ??
 *
 * @param {JQuery} $element Animated element that may contain CSS parallax layers.
 *
 * @returns {void}
 */
export const restoreCssParallaxAnimationEmulation = ($element: JQuery): void => {
  if (isIOSWebKit()) {
    return;
  }

  const owner = $element.get(0);

  if (!(owner instanceof HTMLElement)) {
    return;
  }

  const layers = new Set<HTMLElement>();

  getCssParallaxLayers($element).each((_, layer) => {
    if (layer instanceof HTMLElement) {
      layers.add(layer);
    }
  });

  emulatedCssParallaxLayers.forEach((layer: HTMLElement) => {
    const state = cssParallaxEmulationState.get(layer);

    if (state?.owners.has(owner)) {
      layers.add(layer);
    }
  });

  layers.forEach((layer: HTMLElement) => {
    releaseOwnerFromLayer(layer, owner);
  });
};

/**
 * Whether a layer is currently controlled by CSS parallax animation emulation.
 *
 * @since ??
 *
 * @param {HTMLElement} layer Parallax background span.
 *
 * @returns {boolean} True when emulation currently owns this layer.
 */
export const isCssParallaxAnimationEmulatedLayer = (layer: HTMLElement): boolean =>
  layer.hasAttribute(CSS_PARALLAX_EMULATED_ATTRIBUTE);

/**
 * Remove looping emulation ownership for an owner element and prune detached layers.
 *
 * Use this when looping animations are torn down outside `removeAnimation`, such as
 * Visual Builder module replacement and unmount flows.
 *
 * @since ??
 *
 * @param {JQuery} $element Owner element used when emulation was applied.
 *
 * @returns {void}
 */
export const cleanupCssParallaxAnimationEmulationOwner = ($element: JQuery): void => {
  const owner = $element.get(0);

  syncedCssParallaxLayers.forEach((layer: HTMLElement) => {
    if (owner instanceof HTMLElement) {
      const state = cssParallaxEmulationState.get(layer);

      if (state && state.owners.has(owner)) {
        releaseOwnerFromLayer(layer, owner);
        return;
      }
    }

    if (!layer.isConnected) {
      emulatedCssParallaxLayers.delete(layer);
      syncedCssParallaxLayers.delete(layer);
      cssParallaxEmulationState.delete(layer);
      if (0 === syncedCssParallaxLayers.size) {
        detachViewportSync();
      }
    }
  });
};
